import * as _ from 'lodash';
import { PageBase, PageOptionsBase } from '../../Common/PageBase';
import { DatatableApi, Datatable, DatatableOptions } from '../../Widgets/Datatable';
import { MultiSelectApi } from '../../Widgets/MultiSelect';
import { Utils } from '../../Utils/Utils';
import { RoundType, getRoundTypeValue } from '../../Models/RoundTypeEnum';
import { DateTimePickerApi } from '../../Widgets';

var tableId = 'tableReviews';
var formId = 'formReviews';
var btnCancelSearchId = 'btnCancelSearch';
var tableDom = "<'row'<'col-sm-6'l><'col-sm-6'><'col-sm-6'>>" + "<'row'<'col-sm-12'tr>>" + "<'row'<'col-sm-5'i><'col-sm-7'p>>";

var COLUMNS_INDEX = {
    RoundId: 0,
    Customer: 1,
    TotalWin: 2,
    Source: 3,
    Created: 4,
    Approved: 5,
    ApprovedBy: 6,
    ApprovedDate: 7
}

var FILTER_DELIM = '-delim-';

export interface IndexPageOptions extends PageOptionsBase {
    urlList: string;
    urlDetail: string;
    urlGameRoundDetail: string;
    urlCustomerDetail: string;
    urlProgressiveBonusWinDetail: string;
    urlFreeRoundOfferDetail: string;
}

export class IndexPage extends PageBase<IndexPageOptions> {
    private _tableOptions: DatatableOptions;
    private _table: Datatable;
    private _form: JQuery;
    private _btnCancelSearch: JQuery;

    private _filterId: JQuery;
    private _filterRoundId: JQuery;
    private _filterCustomer: MultiSelectApi;
    private _filterSource: MultiSelectApi;
    private _filterApproved: MultiSelectApi;
    private _filterCreatedFrom: DateTimePickerApi;
    private _filterCreatedTo: DateTimePickerApi;

    constructor(options: IndexPageOptions) {
        super(options);

        var getGameRoundDetailUrl = (roundId: any): string => (`${options.urlGameRoundDetail}/${roundId}`);
        var getProgressiveBonusWinDetailUrl = (roundId: any): string => (`${options.urlProgressiveBonusWinDetail}/${roundId}`);
        var getFreeRoundOfferDetailUrl = (roundId: any): string => (`${options.urlFreeRoundOfferDetail}/${roundId}`);

        var getCustomerDetailUrl = (customerId: any): string => (`${options.urlCustomerDetail}/${customerId}`);

        var getRoundDetailUrl = (rowData: any): string => {
            var resultUrl: string = null;
            switch (getRoundTypeValue(rowData.RoundType)) {
                case RoundType.GameRound:
                    resultUrl = getGameRoundDetailUrl(rowData.RoundId);
                    break;
                case RoundType.ProgressiveBonus:
                    resultUrl = getProgressiveBonusWinDetailUrl(rowData.RoundId);
                    break;
                case RoundType.FreeRoundOffer:
                    resultUrl = getFreeRoundOfferDetailUrl(rowData.RoundId);
                    break;
            }

            return resultUrl;
        };

        this.preDrawCallback = this.preDrawCallback.bind(this);
        this.formSubmit = this.formSubmit.bind(this);
        this.cancelSearch = this.cancelSearch.bind(this);

        // === prepare table options
        this._tableOptions = {
            url: this._options.urlList,
            dom: tableDom,
            rowId: 'Id',
            pageLength: 100,
            lengthMenu: [[20, 50, 100, 200], [20, 50, 100, 200]],
            //autoWidth: true,
            columns: [
                {
                    data: 'RoundId',
                    className: "bold",
                    render: (data: any, type: any, rowData: any) =>
                        (`<a id='lnkGameRound${rowData.Id}' href='${getRoundDetailUrl(rowData)}' target='_blank'>${data}</a>`)
                },
                {
                    data: "CustomerName",
                    className: "bold",
                    render: (data: any, type: any, rowData: any) =>
                        (`<a id='lnkCustomer${rowData.Id}' href='${getCustomerDetailUrl(rowData.CustomerId)}'>${data}</a>`)
                },
                {
                    data: 'TotalWin'
                },
                {
                    data: 'Source'
                },
                {
                    data: 'Created',
                },
                {
                    data: 'Approved',
                    defaultContent: ''
                },
                {
                    data: 'ApprovedBy',
                    defaultContent: ''
                },
                {
                    data: 'ApprovedDate',
                    defaultContent: ''
                },
                {
                    orderable: false,
                    data: 'filterButtons',
                    defaultContent: '',
                    searchable: false,
                    sortable: false
                }
            ],
            order: [[COLUMNS_INDEX.Created, "asc"]],
            callbacks: {
                preDrawCallback: this.preDrawCallback
            }
        };
    }

    init(): void {
        this._form = Utils.getJQueryById(formId);
        this._btnCancelSearch = Utils.getJQueryById(btnCancelSearchId);
        this._filterId = Utils.getJQueryById('filterId');
        this._filterRoundId = Utils.getJQueryById('filterRoundId');
        this._filterCustomer = new MultiSelectApi('filterCustomer');
        this._filterSource = new MultiSelectApi('filterSource');
        this._filterApproved = new MultiSelectApi('filterApproved');
        this._filterCreatedFrom = new DateTimePickerApi("filterCreatedFrom");
        this._filterCreatedTo = new DateTimePickerApi("filterCreatedTo");
        // == create 'new Datatable' instance
        this._table = new Datatable(tableId, this._tableOptions);

        // == attach to events
        // filter form submit -> redraw table
        this._form.on('submit', this.formSubmit);

        // cancel search click -> reset filter row values
        this._btnCancelSearch.on('click', this.cancelSearch);
    }

    private formSubmit(e: JQueryEventObject): any {
        e.preventDefault();
        this._table.draw();
        return false;
    }

    private cancelSearch(e: JQueryEventObject): void {
        this._filterCustomer.selectAll();
        this._filterApproved.selectAll();
        this._filterSource.selectAll();
        this._filterId.val('');
        this._filterRoundId.val('');
        this._filterCreatedFrom.clearDates();
        this._filterCreatedTo.clearDates();
    }

    private preDrawCallback(datatable: DatatableApi): any {
        var filterCustomer = this._filterCustomer.isAllSelected() ? '' : this._filterCustomer.getSelectionValues(FILTER_DELIM);
        datatable.columns(COLUMNS_INDEX.Customer).search(filterCustomer);

        var filterSource = this._filterSource.isAllSelected() ? '' : this._filterSource.getSelectionValues(FILTER_DELIM);
        datatable.columns(COLUMNS_INDEX.Source).search(filterSource);

        var filterApproved = this._filterApproved.isAllSelected() ? '' : this._filterApproved.getSelectionValues(FILTER_DELIM);
        datatable.columns(COLUMNS_INDEX.Approved).search(filterApproved);

        var filterRoundId = this._filterRoundId.val();
        datatable.columns(COLUMNS_INDEX.RoundId).search(filterRoundId);

        var filterCreatedFrom = this._filterCreatedFrom.getUnixTimestamp();
        var filterCreatedTo = this._filterCreatedTo.getUTCDate();
        var filterCreated = filterCreatedFrom + FILTER_DELIM + filterCreatedTo;
        datatable.columns(COLUMNS_INDEX.Created).search(filterCreated);
    }
}


/** WEBPACK FOOTER **
 ** ./Typescript/Pages/Reviews/IndexPage.ts
 **/